/**
 * The faction upgrade simulator (website roadmap S28, after Torn Intel's): Torn's whole upgrade tree from its API (v2
 * `/torn/factiontree`, public — every branch, its upgrades and each level's ability, respect cost and challenge), what
 * your faction already has (v2 `/faction/upgrades`, needs the faction's API access — each branch with Torn's own
 * "respect cost multiplier"), and a plan: the levels you'd add and what they cost.
 *
 * What's certain and what isn't: the tree, the costs and the multipliers are Torn's own numbers. That a planned level
 * costs the tree's cost × its branch's multiplier is our reading of Torn's API docs (it calls the multiplier the
 * "respect cost multiplier"); for a branch your faction hasn't started Torn gives no multiplier — the planner asks for
 * one (1 unless you change it). Shapes are read leniently: a field we can't read is skipped, never a throw.
 */

type Obj = Record<string, unknown>;
const obj = (x: unknown): Obj | null => (x && typeof x === 'object' && !Array.isArray(x) ? (x as Obj) : null);
const arr = (x: unknown): unknown[] => (Array.isArray(x) ? x : obj(x) ? Object.values(x as Obj) : []);
const num = (x: unknown): number | null => (typeof x === 'number' && Number.isFinite(x) ? x : typeof x === 'string' && /^-?\d+(\.\d+)?$/.test(x) ? Number(x) : null);
const str = (x: unknown): string | null => (typeof x === 'string' && x.trim() ? x.trim() : null);

export interface UpgradeLevel {
  level: number;
  /** The level's own name, when Torn gives one. */
  name: string | null;
  ability: string;
  /** Respect, as Torn's tree lists it. */
  cost: number;
  /** A challenge the faction must finish first ("Commit 2,500 crimes"), or null. */
  challenge: string | null;
}

/** One upgrade (Torn's "branch id"): its levels in order. */
export interface UpgradeLine {
  id: number;
  name: string;
  levels: UpgradeLevel[];
}

/** A branch of the tree (Core, Criminality, Aggression…): its upgrades. */
export interface UpgradeBranch {
  name: string;
  lines: UpgradeLine[];
}

/** v2 /torn/factiontree → the branches with their upgrades (empty when it isn't that shape). */
export function readFactionTree(body: unknown): UpgradeBranch[] {
  const out: UpgradeBranch[] = [];
  for (const t of arr(obj(body)?.factionTree ?? obj(body)?.factiontree)) {
    const tree = obj(t);
    const name = str(tree?.name);
    if (!tree || !name) continue;
    const lines: UpgradeLine[] = [];
    for (const b of arr(tree.branches)) {
      const line = obj(b);
      const id = num(line?.id);
      if (!line || id === null) continue;
      const levels = arr(line.upgrades)
        .map((u): UpgradeLevel | null => {
          const o = obj(u);
          const level = num(o?.level);
          const cost = num(o?.cost);
          if (!o || level === null || cost === null) return null;
          const ch = obj(o.challenge);
          const challenge = ch ? (str(ch.description) ?? (num(ch.amount_required) !== null ? `${num(ch.amount_required)!.toLocaleString('en-US')} ${str(ch.stat) ?? ''}`.trim() : null)) : null;
          return { level, name: str(o.name), ability: str(o.ability) ?? '', cost, challenge };
        })
        .filter((l): l is UpgradeLevel => !!l)
        .sort((a, b) => a.level - b.level);
      if (levels.length) lines.push({ id, name: str(line.name) ?? levels[0]!.name ?? `Upgrade ${id}`, levels });
    }
    if (lines.length) out.push({ name, lines });
  }
  return out;
}

export interface FactionUpgradesOwned {
  /** 'peace' or 'war' (which branches are working now), when Torn says. */
  state: string | null;
  /** Each branch the faction has started: Torn's order and respect cost multiplier. */
  branches: Array<{ name: string; order: number | null; multiplier: number | null; kind: 'core' | 'peace' | 'war' }>;
  /** Upgrade id → the highest level the faction has. */
  levels: Record<number, number>;
  /** Ids of the core upgrades it has (Torn gives core upgrades no branch or multiplier). */
  core: number[];
  /** Respect the faction's upgrades cost, as Torn lists each one. */
  spent: number;
}

/** v2 /faction/upgrades → what your faction has (null when it isn't that shape). */
export function readFactionUpgrades(body: unknown): FactionUpgradesOwned | null {
  const up = obj(obj(body)?.upgrades);
  if (!up) return null;
  const out: FactionUpgradesOwned = { state: str(obj(body)?.state), branches: [], levels: {}, core: [], spent: 0 };
  const take = (u: unknown) => {
    const o = obj(u);
    const id = num(o?.id);
    const level = num(o?.level);
    if (!o || id === null || level === null) return;
    out.levels[id] = Math.max(out.levels[id] ?? 0, level);
    out.spent += num(o.cost) ?? 0;
  };
  for (const u of arr(obj(up.core)?.upgrades)) {
    take(u);
    const id = num(obj(u)?.id);
    if (id !== null && !out.core.includes(id)) out.core.push(id);
  }
  for (const kind of ['peace', 'war'] as const) {
    for (const b of arr(up[kind])) {
      const br = obj(b);
      const name = str(br?.name);
      if (!br || !name) continue;
      out.branches.push({ name, order: num(br.order), multiplier: num(br.multiplier), kind });
      for (const u of arr(br.upgrades)) take(u);
    }
  }
  return out;
}

export interface UpgradePlanBranch {
  name: string;
  multiplier: number;
  /** Levels added in this branch. */
  levels: number;
  /** Respect they cost (tree cost × multiplier). */
  cost: number;
}

export interface UpgradePlan {
  branches: UpgradePlanBranch[];
  levels: number;
  cost: number;
  /** Challenges the planned levels need first. */
  challenges: Array<{ upgrade: string; level: number; challenge: string }>;
}

/**
 * A plan: for each upgrade (by id) the level you have and the level you want; every level in between costs its tree
 * cost × its branch's multiplier (`multiplierOf`, 1 when it gives nothing).
 */
export function planUpgrades(tree: readonly UpgradeBranch[], have: Record<number, number>, want: Record<number, number>, multiplierOf: (branch: string) => number | null = () => null): UpgradePlan {
  const plan: UpgradePlan = { branches: [], levels: 0, cost: 0, challenges: [] };
  for (const b of tree) {
    const m = multiplierOf(b.name);
    const multiplier = m !== null && m > 0 ? m : 1;
    const row: UpgradePlanBranch = { name: b.name, multiplier, levels: 0, cost: 0 };
    for (const line of b.lines) {
      const from = have[line.id] ?? 0;
      const to = want[line.id] ?? from;
      for (const l of line.levels) {
        if (l.level <= from || l.level > to) continue;
        row.levels++;
        row.cost += l.cost * multiplier;
        if (l.challenge) plan.challenges.push({ upgrade: line.name, level: l.level, challenge: l.challenge });
      }
    }
    if (row.levels) plan.branches.push(row);
    plan.levels += row.levels;
    plan.cost += row.cost;
  }
  return plan;
}
